import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  ArrowLeft,
  User,
  CreditCard,
  Calendar,
  DollarSign,
  Tag,
  Gift,
  Smartphone,
  Globe,
  FileText,
  AlertCircle,
  CheckCircle,
  Clock,
  RefreshCw,
  ExternalLink,
  Edit,
  Percent,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";

import AdminLayout from "@/components/layout/AdminLayout";
import { fetchUserSubscriptionById } from "@/api/user-subscriptions";
import { UserSubscription } from "@/types/subscription";

const UserSubscriptionDetails = () => {
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const navigate = useNavigate();

  // Fetch subscription details
  const {
    data: subscription,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["userSubscriptionDetails", subscriptionId],
    queryFn: async () => {
      console.log("Fetching subscription with ID:", subscriptionId);
      try {
        const result = await fetchUserSubscriptionById(subscriptionId!);
        console.log("Successfully fetched subscription:", result);
        return result;
      } catch (err) {
        console.error("Error in query function:", err);
        throw err;
      }
    },
    enabled: !!subscriptionId,
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-green-500 hover:bg-green-600";
      case "cancelled":
        return "bg-yellow-500 hover:bg-yellow-600";
      case "expired":
        return "bg-gray-500 hover:bg-gray-600";
      case "pending":
        return "bg-blue-500 hover:bg-blue-600";
      default:
        return "bg-gray-500 hover:bg-gray-600";
    }
  };

  const formatStatusText = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "active":
        return <CheckCircle className="h-4 w-4" />;
      case "cancelled":
        return <AlertCircle className="h-4 w-4" />;
      case "expired":
        return <Clock className="h-4 w-4" />;
      case "pending":
        return <RefreshCw className="h-4 w-4" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };

  if (error) {
    console.error("Error in UserSubscriptionDetails:", error);
    return (
      <AdminLayout title="Subscription Details">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center max-w-md">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              Error Loading Subscription
            </h3>
            <p className="text-muted-foreground mb-2">
              Failed to load subscription details.
            </p>
            <p className="text-sm text-red-600 mb-4 font-mono bg-red-50 p-2 rounded">
              {error instanceof Error
                ? error.message
                : "Unknown error occurred"}
            </p>
            <p className="text-xs text-muted-foreground mb-4">
              Subscription ID: {subscriptionId}
            </p>
            <Button onClick={() => navigate(-1)}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (isLoading) {
    return (
      <AdminLayout title="Subscription Details">
        <div className="space-y-6">
          {/* Header Skeleton */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Skeleton className="h-10 w-10" />
              <div>
                <Skeleton className="h-6 w-48 mb-2" />
                <Skeleton className="h-4 w-32" />
              </div>
            </div>
            <Skeleton className="h-10 w-24" />
          </div>

          {/* Content Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <Skeleton className="h-48" />
              <Skeleton className="h-64" />
            </div>
            <div className="space-y-6">
              <Skeleton className="h-32" />
              <Skeleton className="h-48" />
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!subscription) {
    return (
      <AdminLayout title="Subscription Details">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <CreditCard className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              Subscription Not Found
            </h3>
            <p className="text-muted-foreground mb-4">
              The subscription you're looking for doesn't exist or may have been
              deleted.
            </p>
            <Button onClick={() => navigate(-1)}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Subscription Details">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Subscription Details</h1>
              <p className="text-muted-foreground">
                Subscription ID: {subscription.id}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={getStatusColor(subscription.status)}>
              {getStatusIcon(subscription.status)}
              <span className="ml-1">
                {formatStatusText(subscription.status)}
              </span>
            </Badge>
            {subscription.user && (
              <Button variant="outline" size="sm" asChild>
                <Link to={`/admin/users/${subscription.userId}`}>
                  <ExternalLink className="mr-2 h-4 w-4" />
                  View User
                </Link>
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Subscription Overview */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Subscription Overview
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">
                      Plan
                    </h4>
                    <p className="font-semibold">
                      {subscription.subscriptionPlan?.name || "Unknown Plan"}
                    </p>
                    {subscription.subscriptionPlan?.description && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {subscription.subscriptionPlan.description}
                      </p>
                    )}
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">
                      Duration
                    </h4>
                    <p className="font-semibold">
                      {subscription.subscriptionPlan?.duration}{" "}
                      {subscription.subscriptionPlan?.durationType}
                    </p>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">
                      Price
                    </h4>
                    <p className="font-semibold text-lg">
                      ₹{subscription.price?.toFixed(2)}{" "}
                      {subscription.currencyCode}
                    </p>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">
                      Auto-Renew
                    </h4>
                    <div className="flex items-center gap-2">
                      {subscription.autoRenew ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : (
                        <AlertCircle className="h-4 w-4 text-gray-500" />
                      )}
                      <span className="font-medium">
                        {subscription.autoRenew ? "Enabled" : "Disabled"}
                      </span>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Timeline */}
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-3">
                    Timeline
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Calendar className="h-4 w-4 text-green-500" />
                        <span className="text-sm font-medium">Start Date</span>
                      </div>
                      <p className="font-semibold">
                        {format(
                          new Date(subscription.subscriptionStart),
                          "PPP"
                        )}
                      </p>
                    </div>

                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Calendar className="h-4 w-4 text-red-500" />
                        <span className="text-sm font-medium">End Date</span>
                      </div>
                      <p className="font-semibold">
                        {subscription.subscriptionEnd
                          ? format(
                              new Date(subscription.subscriptionEnd),
                              "PPP"
                            )
                          : "No end date"}
                      </p>
                    </div>

                    {subscription.cancellationDate && (
                      <div className="md:col-span-2">
                        <div className="flex items-center gap-2 mb-1">
                          <AlertCircle className="h-4 w-4 text-yellow-500" />
                          <span className="text-sm font-medium">
                            Cancelled Date
                          </span>
                        </div>
                        <p className="font-semibold">
                          {format(
                            new Date(subscription.cancellationDate),
                            "PPP"
                          )}
                        </p>
                        {subscription.cancellationReason && (
                          <p className="text-sm text-muted-foreground mt-1">
                            Reason: {subscription.cancellationReason}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Discounts & Promotions */}
            {(subscription.totalDiscount && subscription.totalDiscount > 0) ||
            subscription.promotion ? (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Percent className="h-5 w-5" />
                    Discounts & Promotions
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {subscription.totalDiscount &&
                    subscription.totalDiscount > 0 && (
                      <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Tag className="h-4 w-4 text-green-600" />
                          <span className="font-semibold text-green-800 dark:text-green-200">
                            Total Discount Applied
                          </span>
                        </div>
                        <p className="text-2xl font-bold text-green-600">
                          ₹{subscription.totalDiscount.toFixed(2)}
                        </p>

                        {((subscription.promoDiscount &&
                          subscription.promoDiscount > 0) ||
                          (subscription.referralDiscount &&
                            subscription.referralDiscount > 0)) && (
                          <div className="mt-3 space-y-2">
                            {subscription.promoDiscount &&
                              subscription.promoDiscount > 0 && (
                                <div className="flex justify-between">
                                  <span className="text-sm">
                                    Promotional Discount:
                                  </span>
                                  <span className="font-medium text-green-600">
                                    -₹{subscription.promoDiscount.toFixed(2)}
                                  </span>
                                </div>
                              )}
                            {subscription.referralDiscount &&
                              subscription.referralDiscount > 0 && (
                                <div className="flex justify-between">
                                  <span className="text-sm">
                                    Referral Discount:
                                  </span>
                                  <span className="font-medium text-green-600">
                                    -₹{subscription.referralDiscount.toFixed(2)}
                                  </span>
                                </div>
                              )}
                          </div>
                        )}
                      </div>
                    )}

                  {subscription.promotion && (
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-2">
                        Applied Promotion
                      </h4>
                      <div className="border rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold">
                            {subscription.promotion.name}
                          </span>
                          <Badge variant="outline">
                            {subscription.promotion.code}
                          </Badge>
                        </div>
                        {subscription.promotion.description && (
                          <p className="text-sm text-muted-foreground">
                            {subscription.promotion.description}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : null}

            {/* Payment Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Payment Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {subscription.paymentMethod && (
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">
                        Payment Method
                      </h4>
                      <p className="font-semibold">
                        {subscription.paymentMethod.name}
                      </p>
                      {subscription.paymentMethod.description && (
                        <p className="text-sm text-muted-foreground">
                          {subscription.paymentMethod.description}
                        </p>
                      )}
                    </div>
                  )}

                  {subscription.paymentReference && (
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">
                        Payment Reference
                      </h4>
                      <p className="font-mono text-sm bg-muted px-2 py-1 rounded">
                        {subscription.paymentReference}
                      </p>
                    </div>
                  )}
                </div>

                {/* Refund Information */}
                {subscription.refunded && (
                  <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="h-4 w-4 text-amber-600" />
                      <span className="font-semibold text-amber-800 dark:text-amber-200">
                        Refund Information
                      </span>
                    </div>
                    <div className="space-y-1">
                      <div className="flex justify-between">
                        <span>Refund Amount:</span>
                        <span className="font-medium">
                          ₹{subscription.refundAmount?.toFixed(2)}
                        </span>
                      </div>
                      {subscription.refundDate && (
                        <div className="flex justify-between">
                          <span>Refund Date:</span>
                          <span className="font-medium">
                            {format(new Date(subscription.refundDate), "PPP")}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Technical Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Smartphone className="h-5 w-5" />
                  Technical Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {subscription.deviceType && (
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">
                        Device Type
                      </h4>
                      <p className="font-medium">{subscription.deviceType}</p>
                    </div>
                  )}

                  {subscription.osPlatform && (
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">
                        OS Platform
                      </h4>
                      <p className="font-medium">{subscription.osPlatform}</p>
                    </div>
                  )}

                  {subscription.appVersion && (
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">
                        App Version
                      </h4>
                      <p className="font-medium">{subscription.appVersion}</p>
                    </div>
                  )}

                  {subscription.conversionSource && (
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">
                        Conversion Source
                      </h4>
                      <p className="font-medium">
                        {subscription.conversionSource}
                      </p>
                    </div>
                  )}

                  {subscription.acquisitionChannel && (
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">
                        Acquisition Channel
                      </h4>
                      <p className="font-medium">
                        {subscription.acquisitionChannel}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Sidebar */}
          <div className="space-y-6">
            {/* User Information */}
            {subscription.user && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5" />
                    User Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">
                      Name
                    </h4>
                    <p className="font-semibold">{subscription.user.name}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">
                      Email
                    </h4>
                    <p className="font-medium">{subscription.user.email}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">
                      User ID
                    </h4>
                    <p className="font-mono text-sm">{subscription.userId}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Trial Information */}
            {subscription.isTrial && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Gift className="h-5 w-5" />
                    Trial Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                    <p className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                      This is a trial subscription
                    </p>
                    {subscription.trialEndDate && (
                      <div>
                        <span className="text-sm">Trial ends on:</span>
                        <p className="font-semibold">
                          {format(new Date(subscription.trialEndDate), "PPP")}
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Admin Notes */}
            {subscription.adminNotes && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Admin Notes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-muted/50 p-3 rounded-lg">
                    <p className="text-sm whitespace-pre-wrap">
                      {subscription.adminNotes}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Subscription History */}
            {subscription.previousPlanId && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <RefreshCw className="h-5 w-5" />
                    History
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">
                      Previous Plan ID
                    </h4>
                    <p className="font-medium">{subscription.previousPlanId}</p>
                  </div>

                  {subscription.upgradeDate && (
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">
                        Upgrade Date
                      </h4>
                      <p className="font-medium">
                        {format(new Date(subscription.upgradeDate), "PPP")}
                      </p>
                    </div>
                  )}

                  {subscription.downgradeDate && (
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">
                        Downgrade Date
                      </h4>
                      <p className="font-medium">
                        {format(new Date(subscription.downgradeDate), "PPP")}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Metadata */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Metadata
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-1">
                    Created
                  </h4>
                  <p className="font-medium">
                    {format(new Date(subscription.createdAt), "PPp")}
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-1">
                    Last Updated
                  </h4>
                  <p className="font-medium">
                    {format(new Date(subscription.updatedAt), "PPp")}
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-1">
                    Subscription ID
                  </h4>
                  <p className="font-mono text-sm">{subscription.id}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default UserSubscriptionDetails;
